import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

const MAX_POLLS = 8;

/** Polls the payment status after returning from Stripe Checkout. */
export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState("checking"); // checking | paid | timeout | error

    useEffect(() => {
        if (!sessionId) { setState("error"); return; }
        let tries = 0;
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") { setState("paid"); return; }
                if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
            } catch { /* keep trying */ }
            tries += 1;
            if (tries >= MAX_POLLS) { setState("timeout"); return; }
            timer = setTimeout(poll, 2000);
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    return (
        <div className="max-w-md mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
            {state === "checking" && (
                <>
                    <Loader2 className="h-10 w-10 text-[#ff4500] mx-auto mb-4 animate-spin" />
                    <div className="font-display text-3xl tracking-wide">Confirming your payment…</div>
                    <p className="text-sm text-[#a0a0a0] mt-2">This only takes a moment.</p>
                </>
            )}
            {state === "paid" && (
                <>
                    <CheckCircle2 className="h-12 w-12 text-[#22c55e] mx-auto mb-4" />
                    <div className="font-display text-4xl tracking-wide">You're on Pro!</div>
                    <p className="text-sm text-[#a0a0a0] mt-2">Your Pro features are now unlocked.</p>
                    <a href="/account" data-testid="payment-success-account" className="mt-6 inline-block bg-[#ff4500] hover:bg-[#ff5722] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold">Go to my account</a>
                </>
            )}
            {(state === "timeout" || state === "error") && (
                <>
                    <XCircle className="h-12 w-12 text-[#ff4500] mx-auto mb-4" />
                    <div className="font-display text-3xl tracking-wide">{state === "timeout" ? "Still processing" : "Something went wrong"}</div>
                    <p className="text-sm text-[#a0a0a0] mt-2">{state === "timeout" ? "Your payment is taking longer than usual. Check your account in a minute." : "We couldn't confirm this payment."}</p>
                    <a href="/account" className="mt-6 inline-block border border-[#2a2a2a] text-white px-6 py-3 uppercase tracking-[0.2em] text-xs font-semibold hover:border-[#ff4500]">Back to account</a>
                </>
            )}
        </div>
    );
}
